// ---- Kadi support chat (lesson-search.akadian.com) ----
// Replaces the old "Need Help?" button, and covers lesson search too.
//
// The script loads once in the app shell, including for signed-out visitors
// (Kadi runs in public mode until told otherwise). Identity is handed over
// separately via signIn/signOut as auth changes — our token is an ID token
// only; Kadi verifies it once and runs its own self-renewing session (~12h),
// so there is nothing to refresh on our side.
//
// Docs: https://lesson-search.akadian.com/docs/kadi-embedding

const KADI_SRC = "https://lesson-search.akadian.com/kadi.js";

interface KadiApi {
  signIn?: (token: string) => void;
  signOut?: () => void;
  open?: () => void;
  close?: () => void;
  toggle?: () => void;
}

declare global {
  interface Window {
    Kadi?: KadiApi;
  }
}

/** Injects the Kadi script once. Repeat calls are a no-op. */
export function mountKadi(): void {
  if (document.querySelector(`script[src="${KADI_SRC}"]`)) return;

  const script = document.createElement("script");
  script.src = KADI_SRC;
  script.async = true;
  document.body.appendChild(script);
}

/**
 * Hands Kadi the current student's identity, retrying briefly while the vendor
 * script is still loading — mountKadi is async, so the first sign-in usually
 * lands before window.Kadi exists.
 */
export function kadiSignIn(token: string): void {
  withKadi((kadi) => kadi.signIn?.(token));
}

/** Clears Kadi's session when the student signs out. */
export function kadiSignOut(): void {
  withKadi((kadi) => kadi.signOut?.());
}

/**
 * Runs `fn` once window.Kadi is available. Gives up after ~10s rather than
 * polling forever if the script is blocked or the origin is not allowlisted.
 */
function withKadi(fn: (kadi: KadiApi) => void): void {
  if (window.Kadi) {
    fn(window.Kadi);
    return;
  }
  let waited = 0;
  const timer = window.setInterval(() => {
    if (window.Kadi) {
      window.clearInterval(timer);
      fn(window.Kadi);
    } else if ((waited += 200) >= 10_000) {
      window.clearInterval(timer);
    }
  }, 200);
}
